import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface EditableFieldProps {
  label: string;
  value: string;
  mono?: boolean;
  onSave: (value: string) => void;
}

export default function EditableField({ label, value, mono, onSave }: EditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Escape avbryter; blur-en som følger av at feltet forsvinner skal ikke lagre (VIB-2541).
  const cancelled = useRef(false);

  const startEdit = () => {
    cancelled.current = false;
    setDraft(value);
    setEditing(true);
  };

  const handleSave = () => {
    if (cancelled.current) return;
    if (draft !== value) onSave(draft);
    setEditing(false);
  };

  const handleCancel = () => {
    cancelled.current = true;
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <div>
        <p className="text-[11px] text-muted-foreground mb-0.5">{label}</p>
        <input
          aria-label={label}
          className={cn(
            "w-full h-7 rounded border border-input bg-background px-2 text-xs",
            mono && "font-mono"
          )}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") {
              e.stopPropagation();
              handleCancel();
            }
          }}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Rediger ${label.toLowerCase()}`}
      className="cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      onClick={startEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEdit();
        }
      }}
    >
      <p className="text-[11px] text-muted-foreground mb-0.5">{label}</p>
      <p className={cn("text-xs", mono && "font-mono", !value && "text-muted-foreground italic")}>
        {value || "Ikke utfylt"}
      </p>
    </div>
  );
}
